import { useEffect, useState } from "react";
import { Loader2, Package, Plus, FileText, PencilLine, Trash2, Check, X, History, Lock } from "lucide-react";
import { toast } from "sonner";
import { useEmployeeApi } from "../employeeApi";

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Materialeforbrug med handlingsnøgle: samme logiske handling genbruger samme nøgle ved dobbelttryk/genforsøg.
export default function JobMaterials({ jobId, job, onChanged, locked = false }) {
    const api = useEmployeeApi();
    const [stock, setStock] = useState([]);
    const [used, setUsed] = useState(job.materials_used || []);
    const [history, setHistory] = useState(job.materials_history || []);
    const [sel, setSel] = useState("");
    const [qty, setQty] = useState(1);
    const [addKey, setAddKey] = useState(newKey);
    const [busy, setBusy] = useState("");
    const [editing, setEditing] = useState(null); // {entry_id, qty, key}
    const [removeKeys, setRemoveKeys] = useState({});
    const [showHistory, setShowHistory] = useState(false);
    const [report, setReport] = useState(job.report || "");
    const [savingReport, setSavingReport] = useState(false);

    const loadStock = () => api.getMaterials().then((d) => { const m = d.materials || []; setStock(m); setSel((s) => s || m[0]?.id || ""); }).catch(() => setStock([]));
    useEffect(() => { loadStock(); }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

    const apply = (r) => { setUsed(r.materials_used || []); setHistory(r.materials_history || []); loadStock(); onChanged?.(); };

    const add = async () => {
        if (!sel || !(Number(qty) > 0)) return;
        setBusy("add");
        try { apply(await api.addMaterialUse(jobId, sel, Number(qty), addKey)); setQty(1); setAddKey(newKey()); toast.success("Materiale registreret — lager opdateret"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke registrere"); }
        finally { setBusy(""); }
    };
    const saveEdit = async () => {
        if (!editing || !(Number(editing.qty) > 0)) return;
        setBusy(editing.entry_id);
        try { apply(await api.adjustMaterialUse(jobId, editing.entry_id, Number(editing.qty), editing.key)); setEditing(null); toast.success("Antal rettet — lager opdateret"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke rette"); }
        finally { setBusy(""); }
    };
    const remove = async (entryId) => {
        const key = removeKeys[entryId] || newKey();
        setRemoveKeys((k) => ({ ...k, [entryId]: key }));
        setBusy(entryId);
        try { apply(await api.removeMaterialUse(jobId, entryId, key)); toast.success("Fjernet — tilbageført til lager"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Kunne ikke fjerne"); }
        finally { setBusy(""); }
    };
    const saveReport = async () => {
        setSavingReport(true);
        try { await api.saveReport(jobId, report); toast.success("Rapport gemt"); }
        catch { toast.error("Kunne ikke gemme rapporten"); }
        finally { setSavingReport(false); }
    };

    return (
        <>
            <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-materials">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" />Materialeforbrug ({used.length})</h2>
                    {history.length > 0 && <button onClick={() => setShowHistory((v) => !v)} data-testid="materials-history-toggle" className="text-xs text-blue-600 font-semibold flex items-center gap-1"><History className="w-3.5 h-3.5" />Historik</button>}
                </div>
                {locked && (
                    <div className="mb-3 rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600 flex items-center gap-1.5" data-testid="materials-locked-banner">
                        <Lock className="w-3.5 h-3.5" />Låst efter godkendelse — genåbn fakturakladden for at rette materialer.
                    </div>
                )}
                {used.length > 0 && (
                    <ul className="space-y-2 mb-3" data-testid="job-materials-list">
                        {used.map((u) => {
                            const isEdit = editing?.entry_id === u.entry_id;
                            return (
                                <li key={u.entry_id} className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2 text-sm" data-testid={`material-entry-${u.entry_id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-slate-800 truncate">{u.name}</span>
                                        {isEdit ? (
                                            <div className="flex items-center gap-1.5">
                                                <input type="number" min="0.1" step="1" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} data-testid="material-edit-qty" className="w-16 h-8 rounded-lg border border-slate-200 px-2 text-sm text-right bg-white" />
                                                <span className="text-xs text-slate-500">{u.unit}</span>
                                                <button onClick={saveEdit} disabled={busy === u.entry_id} data-testid="material-edit-save" className="w-8 h-8 rounded-lg bg-blue-600 text-white flex items-center justify-center">{busy === u.entry_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}</button>
                                                <button onClick={() => setEditing(null)} data-testid="material-edit-cancel" className="w-8 h-8 rounded-lg border border-slate-200 text-slate-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
                                            </div>
                                        ) : (
                                            <span className="font-semibold text-slate-700 shrink-0" data-testid="material-entry-qty">{u.qty} {u.unit}</span>
                                        )}
                                    </div>
                                    {!isEdit && !locked && (
                                        <div className="flex gap-3 mt-1.5">
                                            <button onClick={() => setEditing({ entry_id: u.entry_id, qty: u.qty, key: newKey() })} data-testid="material-edit" className="text-xs font-semibold text-blue-600 flex items-center gap-1"><PencilLine className="w-3 h-3" />Ret antal</button>
                                            <button onClick={() => remove(u.entry_id)} disabled={busy === u.entry_id} data-testid="material-remove" className="text-xs font-semibold text-red-600 flex items-center gap-1 disabled:opacity-50">{busy === u.entry_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}Fjern</button>
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
                {showHistory && history.length > 0 && (
                    <ul className="mb-3 rounded-xl border border-dashed border-slate-200 p-2.5 space-y-1 text-xs text-slate-500" data-testid="materials-history">
                        {history.slice().reverse().map((x, i) => (
                            <li key={i}>{x.time} · {x.by} <span className="font-semibold text-slate-700">{x.action}</span> {x.name} ({x.delta > 0 ? "+" : ""}{x.delta} {x.unit})</li>
                        ))}
                    </ul>
                )}
                <div className="flex gap-2">
                    <select value={sel} onChange={(e) => { setSel(e.target.value); setAddKey(newKey()); }} data-testid="material-select" disabled={locked}
                        className="flex-1 h-10 rounded-lg border border-slate-200 px-2 text-sm bg-white min-w-0 disabled:opacity-50">
                        {stock.map((m) => <option key={m.id} value={m.id}>{`${m.name} (${m.stock} ${m.unit})`}</option>)}
                    </select>
                    <input type="number" min="0" step="1" value={qty} onChange={(e) => { setQty(e.target.value); setAddKey(newKey()); }} data-testid="material-qty" disabled={locked} className="w-16 h-10 rounded-lg border border-slate-200 px-2 text-sm text-right disabled:opacity-50" />
                    <button onClick={add} disabled={busy === "add" || !sel || locked} data-testid="material-add" className="h-10 px-3 rounded-lg bg-blue-600 text-white font-semibold flex items-center gap-1 disabled:opacity-50">
                        {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    </button>
                </div>
                <p className="text-[11px] text-slate-400 mt-2">Lager og registrering opdateres samlet. Rettelser opdaterer kun fakturakladden.</p>
            </section>

            <section className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid="job-report">
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" />Rapport</h2>
                <textarea value={report} onChange={(e) => setReport(e.target.value)} rows={3} placeholder="Beskriv udført arbejde…" data-testid="report-input"
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none" />
                <button onClick={saveReport} disabled={savingReport} data-testid="report-save"
                    className="mt-2 w-full h-10 rounded-xl bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                    {savingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Gem rapport
                </button>
            </section>
        </>
    );
}
